import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { areUpdatesHeld } from "@/lib/updateHold";

/**
 * Adopts a downloaded update at the next page navigation.
 *
 * Moving between pages here is client-side routing — no new document is
 * fetched — so a visitor browsing the public site could stay on an old build
 * indefinitely. Dashboard flows hid this: signing in, signing out, and the
 * "Home" buttons all do full document loads, so those users picked up new
 * builds as a side effect and the public site appeared not to update.
 *
 * When a new worker has taken control (window.__swUpdatePending, set in
 * src/main.tsx), turn the next route change into a real load. The user is
 * already leaving the page they were on, so nothing is interrupted.
 */
export function RouteChangeUpdater() {
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);

  useEffect(() => {
    // Ignore the first render — only act on an actual navigation. Tracking
    // the PREVIOUS path (not the initial one) so that navigating back to the
    // page the visit started on still counts as a navigation.
    if (pathname === lastPath.current) return;
    lastPath.current = pathname;
    if (!window.__swUpdatePending) return;
    // A screen may still be holding work open across this navigation (a form
    // in a dialog, say). Leave the flag set so the next break takes it.
    if (areUpdatesHeld()) return;

    window.__swUpdatePending = false;
    window.location.reload();
  }, [pathname]);

  return null;
}
